import { useMemo, useRef, useState } from 'react';
import { Box, Typography, Chip, Alert, Button } from '@mui/material';
import { Hub, LinkOff } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { mcpConnectionsService, McpConnection } from '../../services/mcp-connections.service';
import { useAuthStore } from '../../stores/auth.store';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import ConfirmDialog from '../../components/ConfirmDialog';
import EntityGridToolbar from '../../components/EntityGridToolbar';

/** The endpoints are manager-or-higher, and a control that answers 403 is worse than none. */
export function useMayManageConnections(): boolean {
  return useAuthStore((s) => s.user?.role) === 'admin';
}

export default function McpConnectionsPage() {
  const queryClient = useQueryClient();
  const mayManage = useMayManageConnections();
  const [pending, setPending] = useState<McpConnection | null>(null);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);

  const { data, isLoading, error } = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: mcpConnectionsService.list,
    enabled: mayManage,
  });

  const revoke = useMutation({
    mutationFn: mcpConnectionsService.revoke,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['mcp-connections'] });
      setPending(null);
      enqueueSnackbar(`Revoked ${res.revoked} connection${res.revoked === 1 ? '' : 's'}`, { variant: 'success' });
    },
    onError: (err: unknown) => {
      setPending(null);
      enqueueSnackbar(err instanceof Error ? err.message : 'The revoke was refused', { variant: 'error' });
    },
  });

  if (!mayManage) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          MCP Connections
        </Typography>
        <Alert severity="warning">You do not have access to this page.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
          MCP Connections
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Revoking stops the client from renewing access, but an already-issued access token
          remains valid until it expires (about an hour).
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>Failed to load MCP connections.</Alert>}
      </Box>

      <ConnectionsGrid
        rows={data ?? []} loading={isLoading} search={search} onSearch={setSearch}
        height={gridHeight} onRevoke={setPending}
      />

      <ConfirmDialog
        open={!!pending}
        title="Revoke connection"
        destructive
        warning="An already-issued access token stays valid until it expires (about an hour)."
        body={
          pending
            ? `Revoke ${pending.client_name} for ${pending.user} in every workspace? The client will no longer be able to renew access.`
            : ''
        }
        confirmLabel="Revoke"
        busyLabel="Revoking..."
        busy={revoke.isPending}
        onConfirm={() => pending && revoke.mutate({ client_id: pending.client_id, user_id: pending.user_id })}
        onClose={() => setPending(null)}
      />
    </Box>
  );
}

function NoConnections() {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Hub sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography color="text.secondary">No active MCP connections</Typography>
    </Box>
  );
}

function ConnectionsGrid({ rows, loading, search, onSearch, height, onRevoke }: {
  rows: McpConnection[];
  loading: boolean;
  search: string;
  onSearch: (v: string) => void;
  height: number;
  onRevoke: (row: McpConnection) => void;
}) {
  // The API returns every connection at once, so search filters here.
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.client_name, r.client_id, r.user]
      .some((v) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const columns: GridColDef<McpConnection>[] = [
    {
      field: 'client_name', headerName: 'Client', flex: 1, minWidth: 220,
      renderCell: (p: GridRenderCellParams<McpConnection>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Hub color="primary" />
          <Box sx={{ lineHeight: 1.2 }}>
            <Typography variant="body2" fontWeight={500}>{p.row.client_name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{p.row.client_id}</Typography>
          </Box>
        </Box>
      ),
    },
    { field: 'user', headerName: 'User', width: 180 },
    {
      field: 'sessions', headerName: 'Sessions', type: 'number', width: 100,
      renderCell: (p: GridRenderCellParams<McpConnection>) => <Chip label={p.value} size="small" />,
    },
    { field: 'first_connected', headerName: 'First connected', width: 190, type: 'dateTime', valueGetter: (v: string | null) => (v ? new Date(v) : null) },
    { field: 'last_activity', headerName: 'Last activity', width: 190, type: 'dateTime', valueGetter: (v: string | null) => (v ? new Date(v) : null) },
    {
      field: 'actions', headerName: '', width: 120, sortable: false, disableColumnMenu: true, align: 'right',
      renderCell: (p: GridRenderCellParams<McpConnection>) => (
        <Button size="small" color="error" startIcon={<LinkOff sx={{ fontSize: 16 }} />} onClick={() => onRevoke(p.row)}>
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <DataGridPremium
      rows={shown}
      columns={columns}
      loading={loading}
      getRowId={(r: McpConnection) => `${r.client_id}:${r.user_id}`}
      rowHeight={60}
      initialState={{ sorting: { sortModel: [{ field: 'last_activity', sort: 'desc' }] } }}
      showToolbar
      slots={{ toolbar: EntityGridToolbar, noRowsOverlay: NoConnections }}
      slotProps={{ toolbar: { search, onSearch } }}
      disableRowSelectionOnClick
      sx={{ bgcolor: 'background.paper', borderRadius: 2, height, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
    />
  );
}
